import { AlertCircle, Bot, Clock } from "lucide-react";
import React from "react";
import { AI_CODE_REVIEWER_USERNAME } from "../constants";
import { Avatar } from "./Avatar";
import { Tooltip } from "./Tooltip";

const isAiCodeReviewer = (reviewer) =>
	reviewer?.username === AI_CODE_REVIEWER_USERNAME;

const AiReviewMissingStatus = () => (
	<Tooltip content="AI Code Review missing">
		<span className="inline-flex items-center justify-center relative w-5 h-5 rounded-full bg-amber-500/10 ring-1 ring-amber-500/60 text-amber-600 dark:text-amber-400 monokai:text-monokai-yellow dracula:text-dracula-yellow nord:text-nord-orange">
			<Bot className="w-3.5 h-3.5" />
			<AlertCircle className="absolute -right-1 -bottom-1 w-2.5 h-2.5 rounded-full bg-[var(--color-bg-primary)]" />
		</span>
	</Tooltip>
);

const HumanApprovalStatus = ({
	reviewers,
	approvers,
	approvalsLeft,
	subtle,
}) => {
	if (approvers.length === 0) {
		if (reviewers.length === 0) {
			return (
				<Tooltip content="No human reviewers assigned!">
					<span className="inline-flex items-center relative">
						<Clock
							className="w-4 h-4 text-gray-500 dark:text-gray-400
								monokai:text-monokai-foreground/70
								dracula:text-dracula-foreground/70
								nord:text-nord-snow-storm-1/70
								synthwave:text-synthwave-foreground/70
								aura:text-aura-foreground/70"
						/>
						<AlertCircle className="absolute -top-1 -right-1 w-2.5 h-2.5 text-red-500" />
					</span>
				</Tooltip>
			);
		}

		return (
			<Tooltip content="Waiting for human approvals">
				<span className="inline-flex items-center">
					<Clock
						className="w-4 h-4 text-gray-500 dark:text-gray-400
							monokai:text-monokai-foreground/70
							dracula:text-dracula-foreground/70
							nord:text-nord-snow-storm-1/70
							synthwave:text-synthwave-foreground/70
							aura:text-aura-foreground/70"
					/>
				</span>
			</Tooltip>
		);
	}

	const hasOneApproval = approvers.length === 1;
	const bgColor = subtle
		? approvalsLeft === 0
			? "bg-emerald-500/10"
			: "bg-amber-500/10"
		: approvalsLeft === 0
			? "bg-opacity-10 dark:bg-opacity-15 bg-green-500 dark:bg-green-500 monokai:bg-monokai-green dracula:bg-dracula-green nord:bg-nord-green synthwave:bg-synthwave-green aura:bg-aura-cyan"
			: "bg-opacity-10 dark:bg-opacity-15 bg-yellow-500 dark:bg-yellow-500 monokai:bg-monokai-yellow dracula:bg-dracula-yellow nord:bg-nord-orange synthwave:bg-synthwave-yellow aura:bg-aura-yellow";
	const ringColor = subtle
		? approvalsLeft === 0
			? "ring-emerald-500/40"
			: "ring-amber-500/40"
		: approvalsLeft === 0
			? hasOneApproval
				? "ring-green-300 dark:ring-green-300 monokai:ring-monokai-green dracula:ring-dracula-green nord:ring-nord-green synthwave:ring-synthwave-green aura:ring-aura-cyan"
				: "ring-green-500 dark:ring-green-500 monokai:ring-monokai-green dracula:ring-dracula-green nord:ring-nord-green synthwave:ring-synthwave-green aura:ring-aura-cyan"
			: "ring-yellow-500 dark:ring-yellow-500 monokai:ring-monokai-yellow dracula:ring-dracula-yellow nord:ring-nord-orange synthwave:ring-synthwave-yellow aura:ring-aura-yellow";
	const borderColor = subtle
		? "border-[var(--response-bubble)]"
		: approvalsLeft === 0
			? hasOneApproval
				? "border-green-200 dark:border-green-200 monokai:border-monokai-green dracula:border-dracula-green nord:border-nord-green synthwave:border-synthwave-green aura:border-aura-cyan"
				: "border-green-500 dark:border-green-400 monokai:border-monokai-green dracula:border-dracula-green nord:border-nord-green synthwave:border-synthwave-green aura:border-aura-cyan"
			: "border-yellow-500 dark:border-yellow-400 monokai:border-monokai-yellow dracula:border-dracula-yellow nord:border-nord-orange synthwave:border-synthwave-yellow aura:border-aura-yellow";

	const content = (
		<div
			className={`flex -space-x-1.5 rounded-full px-2.5 py-1 ring-1 ${bgColor} ${ringColor}`}
		>
			{approvers.map((reviewer) => (
				<Tooltip
					key={reviewer.username || reviewer.name}
					content={`Approved by ${reviewer.name}`}
				>
					<Avatar
						src={reviewer.avatarUrl}
						alt={reviewer.name}
						size="sm"
						className={borderColor}
					/>
				</Tooltip>
			))}
		</div>
	);

	return approvalsLeft > 0 ? (
		<Tooltip content="More approvals are needed">{content}</Tooltip>
	) : (
		content
	);
};

export const ApprovalStatus = ({
	reviewers,
	approvers,
	approvalsLeft,
	aiReviewMissing,
	subtle = false,
}) => {
	const humanApprovers = (approvers || []).filter(
		(reviewer) => !isAiCodeReviewer(reviewer),
	);
	const humanReviewers = (reviewers || []).filter(
		(reviewer) => !isAiCodeReviewer(reviewer),
	);
	const humanApprovalsLeft =
		typeof approvalsLeft === "number"
			? Math.max(0, approvalsLeft - (aiReviewMissing ? 1 : 0))
			: approvalsLeft;

	return (
		<span className="inline-flex items-center gap-2">
			{aiReviewMissing && <AiReviewMissingStatus />}
			<HumanApprovalStatus
				approvers={humanApprovers}
				reviewers={humanReviewers}
				approvalsLeft={humanApprovalsLeft}
				subtle={subtle}
			/>
		</span>
	);
};

export default ApprovalStatus;
